
import { useEffect, useRef, useState } from 'react';

type WordState = 'visible' | 'expanding' | 'hidden' | 'regenerating';

interface WordData {
  text: string;
  state: WordState;
}

function ExplodingHeading() {
  const words = ['Pleadings', 'Contracts', 'Discovery', 'Done'];
  const [wordStates, setWordStates] = useState<WordData[]>(
    words.map((text) => ({ text, state: 'visible' }))
  );
  const [phase, setPhase] = useState<'idle' | 'expanding' | 'regenerating'>('idle');
  const [currentIndex, setCurrentIndex] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const startCycle = () => {
      timeoutRef.current = setTimeout(() => {
        setPhase('expanding');
        setCurrentIndex(words.length - 1);
      }, 2000);
    };
    startCycle();
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  useEffect(() => {
    if (phase === 'expanding') {
      if (currentIndex >= 0) {
        setWordStates((prev) => prev.map((w, i) =>
          i === currentIndex ? { ...w, state: 'expanding' } : w
        ));
        timeoutRef.current = setTimeout(() => {
          setWordStates((prev) => prev.map((w, i) =>
            i === currentIndex ? { ...w, state: 'hidden' } : w
          ));
          setCurrentIndex((p) => p - 1);
        }, 800);
      } else {
        timeoutRef.current = setTimeout(() => {
          setPhase('regenerating');
          setCurrentIndex(0);
        }, 400);
      }
    }

    if (phase === 'regenerating') {
      if (currentIndex < words.length) {
        setWordStates((prev) => prev.map((w, i) =>
          i === currentIndex ? { ...w, state: 'regenerating' } : w
        ));
        timeoutRef.current = setTimeout(() => {
          setWordStates((prev) => prev.map((w, i) =>
            i === currentIndex ? { ...w, state: 'visible' } : w
          ));
          setCurrentIndex((p) => p + 1);
        }, 500);
      } else {
        timeoutRef.current = setTimeout(() => {
          setPhase('expanding');
          setCurrentIndex(words.length - 1);
        }, 2000);
      }
    }

    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [phase, currentIndex, words.length]);

  return (
    <h2 className="text-[#09090b] text-[40px] sm:text-[56px] font-bold leading-tight mb-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
      {wordStates.map((word, idx) => (
        <span key={idx} className="relative inline-block">
          <span
            className={`inline-block transition-all duration-700 ease-out ${
              word.state === 'visible'
                ? 'opacity-100 scale-100'
                : word.state === 'expanding'
                ? 'opacity-0 scale-150'
                : word.state === 'regenerating'
                ? 'opacity-0 scale-50'
                : 'opacity-0 scale-150'
            }`}
          >
            {word.text}
          </span>

          {word.state === 'regenerating' && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="animate-word-appear text-[#8B4513] drop-shadow-[0_0_20px_rgba(139,69,19,0.8)]">
                {word.text}
              </span>
            </span>
          )}
        </span>
      ))}

      <style>{`
        @keyframes wordAppear {
          0% {
            opacity: 0;
            transform: scale(0.3);
            filter: blur(8px);
            color: #8B4513;
          }
          50% {
            opacity: 1;
            transform: scale(1.15);
            filter: blur(0px);
            color: #8B4513;
          }
          100% {
            opacity: 1;
            transform: scale(1);
            filter: blur(0px);
            color: #09090b;
          }
        }
        .animate-word-appear {
          animation: wordAppear 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
    </h2>
  );
}

export default function WhatYouCanCreate() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const documents = [
    {
      title: 'Motions',
      description: 'Dictate the facts, we draft the argument. Compel. Dismiss. Summary judgment.',
      delay: '0ms'
    },
    {
      title: 'Contracts',
      description: 'NDAs. Service agreements. Settlement docs. Speak the terms, download the draft.',
      delay: '200ms'
    },
    {
      title: 'Discovery',
      description: 'Interrogatories. Requests for production. Four minutes instead of forty.',
      delay: '400ms'
    },
    {
      title: 'Briefs',
      description: 'Upload opposing counsel\'s motion. Tell us your angle. Get a court-ready response.',
      delay: '600ms'
    }
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-32 px-8 bg-white"
    >
      <div className="max-w-[1200px] mx-auto">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <ExplodingHeading />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {documents.map((doc, index) => (
            <div
              key={index}
              className={`group bg-[#f5f0e8] border border-[#e8dcc8] rounded-2xl p-10 hover:border-[#8B4513] hover:shadow-[0_0_40px_rgba(139,69,19,0.15)] hover:-translate-y-2 transition-all duration-500 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: isVisible ? doc.delay : '0ms' }}
            >
              <h3 className="text-[#09090b] text-[28px] font-bold mb-4 group-hover:text-[#8B4513] transition-colors">
                {doc.title}
              </h3>
              <p className="text-[#52525b] text-[17px] leading-relaxed">
                {doc.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
